import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { ReactComponent as LarIc } from '../../assets/svg/lar_ic.svg';
import { ReactComponent as RarIc } from '../../assets/svg/rar_ic.svg';
import {
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { apiCall } from '../../services/api-service';
import './TransactionsChart.css';

const TABS = ['Daily', 'Monthly', 'Quarterly', 'Yearly'];

const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const Y_LABELS = ['1K', '500', '200', '100', '50', '25', '0'];

const BREAKPOINTS = [0, 25, 50, 100, 200, 500, 1000];

const toScale6 = (value) => {
  const max = BREAKPOINTS[BREAKPOINTS.length - 1];
  const clamped = Math.min(Math.max(value, 0), max);
  for (let i = 1; i < BREAKPOINTS.length; i++) {
    if (clamped <= BREAKPOINTS[i]) {
      const lo = BREAKPOINTS[i - 1];
      const hi = BREAKPOINTS[i];
      return (i - 1) + (clamped - lo) / (hi - lo);
    }
  }
  return 6;
};

const formatAmount = (v) => {
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(0)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(0)},00,000`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(0)}K`;
  return `₹${v}`;
};

const getFullLabel = (shortLabel, activeTab, activeYear) => {
  if (activeTab === 'Monthly') {
    const months = {
      Jan: 'January', Feb: 'February', Mar: 'March', Apr: 'April', May: 'May', Jun: 'June',
      Jul: 'July', Aug: 'August', Sep: 'September', Oct: 'October', Nov: 'November', Dec: 'December'
    };
    return `${months[shortLabel] || shortLabel}, ${activeYear}`;
  }
  if (activeTab === 'Daily') {
    const days = {
      Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday'
    };
    return `${days[shortLabel] || shortLabel}, ${activeYear}`;
  }
  if (activeTab === 'Quarterly') {
    const quarters = {
      Q1: 'First Quarter', Q2: 'Second Quarter', Q3: 'Third Quarter', Q4: 'Fourth Quarter'
    };
    return `${quarters[shortLabel] || shortLabel}, ${activeYear}`;
  }
  if (activeTab === 'Yearly') {
    return shortLabel;
  }
  return `${shortLabel}, ${activeYear}`;
};

const TooltipCard = ({ title, cardType, seriesLabel, value, amountValue }) => (
  <div className={`transactions-tooltip-card ${cardType}`}>
    <span className={`transactions-tooltip-title ${cardType}`}>
      {title}
    </span>
    <div className="transactions-tooltip-body">
      <div className="transactions-tooltip-row">
        <div className="d-flex align-items-center gap-[6px]">
          <span className={`transactions-tooltip-dot ${cardType}`}></span>
          <span className="transactions-tooltip-label">{seriesLabel}</span>
        </div>
        <span className="transactions-tooltip-value">{value}</span>
      </div>
      <div className="transactions-tooltip-row">
        <div className="d-flex align-items-center gap-[6px]">
          <span className="transactions-tooltip-dot amount"></span>
          <span className="transactions-tooltip-label">Amount</span>
        </div>
        <span className="transactions-tooltip-value">{amountValue}</span>
      </div>
    </div>
  </div>
);

const CustomTooltip = ({ active, payload, activeTab, year, coordinate }) => {
  if (!active || !payload?.length) return null;

  const data = payload[0].payload;
  const title = getFullLabel(data.month, activeTab, year);
  const txnVal = data.transactions;
  const paymentVal = data.payments;
  const amtVal = formatAmount(data.amount);

  let translateX = -217;
  if (coordinate) {
    const width = 434;
    const wrapper = typeof document !== 'undefined' ? document.querySelector('.transactions-chart-container .recharts-wrapper') : null;
    const wrapperLeft = wrapper ? wrapper.getBoundingClientRect().left : 276;
    const minViewportLeft = 264;
    const minLeft = minViewportLeft - wrapperLeft;
    const wrapperWidth = wrapper ? wrapper.clientWidth : 800;
    const maxRight = wrapperWidth - 12;
    const x = coordinate.x;
    const minTranslate = minLeft - x;
    const maxTranslate = maxRight - x - width;
    translateX = Math.max(minTranslate, Math.min(maxTranslate, -217));
  }

  return (
    <div className="transactions-tooltip-wrapper" style={{ transform: `translateX(${translateX}px)` }}>
      <TooltipCard
        title={title}
        cardType="payments"
        seriesLabel="Payments"
        value={paymentVal}
        amountValue={amtVal}
      />
      <TooltipCard
        title={title}
        cardType="transactions"
        seriesLabel="Transactions"
        value={txnVal}
        amountValue={amtVal}
      />
    </div>
  );
};

const CustomCursor = (props) => {
  const { points, height } = props;
  if (!points || !points[0]) return null;
  const { x, y } = points[0];
  return (
    <g>
      <line
        x1={x}
        y1={y}
        x2={x}
        y2={y + height}
        stroke="#4F46E5"
        strokeWidth={1}
        strokeDasharray="4 3"
      />
      <circle
        cx={x}
        cy={y + height}
        r={5}
        fill="#FFFFFF"
        stroke="#212129"
        strokeWidth={2}
      />
    </g>
  );
};

export const TransactionsChart = () => {
  const [activeTab, setActiveTab] = useState('Monthly');
  const [monthIndex, setMonthIndex] = useState(5);
  const [year, setYear] = useState(2024);
  const [activePoint, setActivePoint] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTransactionChart = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiCall('get', `dashboard/transaction-chart?period=${activeTab.toLowerCase()}`);
        if (res?.data?.status === 'success' || res?.data?.success === true) {
          setChartData(res.data.data || []);
        } else {
          setError(res?.data?.message || 'Failed to load chart data');
        }
      } catch (err) {
        console.error('Error fetching transaction chart:', err);
        setError('Error fetching transaction data');
      } finally {
        setLoading(false);
      }
    };
    fetchTransactionChart();
  }, [activeTab]);

  const data = chartData;

  const scale6Data = data.map((d) => ({
    ...d,
    txnScale: toScale6(d.transactions),
    paymentScale: toScale6(d.payments),
  }));

  const prevMonth = () => {
    setMonthIndex((i) => {
      if (i === 0) { setYear((y) => y - 1); return 11; }
      return i - 1;
    });
  };

  const nextMonth = () => {
    setMonthIndex((i) => {
      if (i === 11) { setYear((y) => y + 1); return 0; }
      return i + 1;
    });
  };

  return (
    <div className="transactions-chart-card">
      <div className="transactions-chart-header">
        <h3 className="transactions-chart-title">
          Transactions &amp; Payments Overview
        </h3>

        <div className="transactions-chart-nav">
          <span className="transactions-chart-nav-text">
            {MONTH_LABELS[monthIndex].slice(0, 3)}, {year}
          </span>

          <div className="transactions-chart-nav-buttons">
            <button
              onClick={prevMonth}
              className="transactions-chart-nav-btn"
              aria-label="Previous month"
            >
              <LarIc style={{ width: '16px', height: '16px', display: 'block' }} />
            </button>
            <button
              onClick={nextMonth}
              className="transactions-chart-nav-btn"
              aria-label="Next month"
            >
              <RarIc style={{ width: '16px', height: '16px', display: 'block' }} />
            </button>
          </div>
        </div>
      </div>

      <div className="transactions-chart-tabs">
        {TABS.map((tab) => {
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className="transactions-chart-tab"
              style={{
                fontWeight: isActive ? 600 : 400,
                background: isActive ? '#ECEBFF' : '#F1F3F6',
                color: isActive ? '#4F46E5' : '#212129',
              }}
            >
              {tab}
            </button>
          );
        })}
      </div>

      <div className="transactions-chart-body">
        {loading ? (
          <div className="transactions-chart-loading">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : error ? (
          <div className="transactions-chart-error">
            {error}
          </div>
        ) : !data || data.length === 0 ? (
          <div className="transactions-chart-empty">
            No transactions found.
          </div>
        ) : (
          <>
            <div className="transactions-chart-row">
              <div className="transactions-chart-yaxis">
                {Y_LABELS.map((label) => (
                  <div key={label} className="transactions-chart-yaxis-label">
                    {label}
                  </div>
                ))}
              </div>

              <div className="transactions-chart-plot transactions-chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={scale6Data}
                    margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
                    onMouseMove={(state) => {
                      if (state?.activeLabel) setActivePoint(state.activeLabel);
                    }}
                    onMouseLeave={() => setActivePoint(null)}
                  >
                    <defs>
                      <linearGradient id="txnGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#4F46E5" stopOpacity={0.28} />
                        <stop offset="100%" stopColor="#4F46E5" stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="amtGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#4EBD7E" stopOpacity={0.28} />
                        <stop offset="100%" stopColor="#4EBD7E" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>

                    <CartesianGrid
                      strokeDasharray="4 4"
                      stroke="#D7DFE4"
                      strokeWidth={1}
                      vertical={false}
                    />

                    <YAxis
                      domain={[0, 6]}
                      ticks={[0, 1, 2, 3, 4, 5, 6]}
                      hide
                    />

                    <XAxis dataKey="month" hide />

                    <Tooltip
                      content={<CustomTooltip activeTab={activeTab} year={year} />}
                      cursor={<CustomCursor />}
                    />

                    <Area
                      type="monotone"
                      dataKey="txnScale"
                      stroke="#4F46E5"
                      strokeWidth={1.5}
                      fill="url(#txnGrad)"
                      dot={false}
                      activeDot={{ r: 5, fill: '#4F46E5', stroke: '#fff', strokeWidth: 2 }}
                    />

                    <Area
                      type="monotone"
                      dataKey="paymentScale"
                      stroke="#4EBD7E"
                      strokeWidth={1.5}
                      fill="url(#amtGrad)"
                      dot={false}
                      activeDot={{ r: 5, fill: '#4EBD7E', stroke: '#fff', strokeWidth: 2 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="transactions-chart-xaxis">
              {data.map((d) => {
                const isActive = d.month === activePoint;
                return (
                  <div
                    key={d.month}
                    className={`transactions-chart-xaxis-label ${isActive ? 'active' : ''}`}
                  >
                    {d.month}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

